import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { IRetailerSlice } from '@lib/core/retailers/slices/retailer';
import { retailerDataApiUrlCreator } from '@lib/core/retailers/slices/urls';
import { IRetailerDetail } from '@lib/core/retailers/types/retailerDetail';
import { TServiceInstanceType } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { request } from '@lib/core/service/requests/request';

export interface IServiceInstance {
  retailer: IRetailerSlice;
  serviceInstanceProductCategory: string;
  serviceInstanceRetailerLocationId: string;
  serviceInstanceRetailerSlug: string;
  serviceInstanceRetailerLocationSlug: string;
  serviceInstanceType: Record<TServiceInstanceType, boolean>;
}

export const initialState: IServiceInstance = {
  retailer: {
    data: undefined,
    error: null,
    isLoading: false,
  },
  serviceInstanceProductCategory: '',
  serviceInstanceRetailerLocationId: '',
  serviceInstanceRetailerLocationSlug: '',
  serviceInstanceRetailerSlug: '',
  serviceInstanceType: {
    isServiceInstanceDutyFree: false,
    isServiceInstanceShortRecommender: false,
  },
};

export const actionGetServiceInstanceRetailerData = createTypedAsyncThunk(
  'serviceInstance/retailer/actionGetServiceInstanceRetailerData',
  async ({
    serviceInstanceRetailerSlug,
  }: {
    serviceInstanceRetailerSlug: string;
    serviceInstanceProductCategory: string;
    serviceInstanceRetailerLocationId: string;
    serviceInstanceRetailerLocationSlug: string;
  }) => request({ url: retailerDataApiUrlCreator({ retailerSlug: serviceInstanceRetailerSlug }) }),
);

const serviceInstance = createSlice({
  extraReducers: builder => {
    builder.addCase(actionGetServiceInstanceRetailerData.pending, (state, { meta: { arg } }) => {
      const {
        serviceInstanceProductCategory,
        serviceInstanceRetailerLocationId,
        serviceInstanceRetailerSlug,
        serviceInstanceRetailerLocationSlug,
      } = arg;

      state.retailer.error = null;
      state.retailer.isLoading = true;
      state.serviceInstanceProductCategory = serviceInstanceProductCategory;
      state.serviceInstanceRetailerLocationId = serviceInstanceRetailerLocationId;
      state.serviceInstanceRetailerSlug = serviceInstanceRetailerSlug;
      state.serviceInstanceRetailerLocationSlug = serviceInstanceRetailerLocationSlug;
    });
    builder.addCase(actionGetServiceInstanceRetailerData.fulfilled, (state, action: PayloadAction<IRetailerDetail>) => {
      const { payload } = action;
      state.retailer.isLoading = false;
      state.retailer.data = payload;
    });
    builder.addCase(actionGetServiceInstanceRetailerData.rejected, (state, action) => {
      state.retailer.isLoading = false;
      state.retailer.error = action.error;
    });
  },
  initialState,
  name: 'serviceInstance',
  reducers: {
    actionUpdateServiceInstance: (
      state,
      {
        payload,
      }: {
        payload: {
          serviceInstanceRetailerSlug?: string;
          serviceInstanceType?: TServiceInstanceType;
        };
      },
    ) => {
      if (payload.serviceInstanceType) {
        state.serviceInstanceType = {
          isServiceInstanceDutyFree: false,
          isServiceInstanceShortRecommender: false,
          [payload.serviceInstanceType]: true,
        };
      }

      if (payload.serviceInstanceRetailerSlug || payload.serviceInstanceRetailerSlug === '') {
        state.serviceInstanceRetailerSlug = payload.serviceInstanceRetailerSlug;
      }
    },
  },
});
export const { actionUpdateServiceInstance } = serviceInstance.actions;

export default serviceInstance.reducer;
